import { useMemoizedFn } from 'ahooks';
import { AxiosInstance } from 'axios';
import { Draft } from 'immer';
import { WritableDraft } from 'immer/dist/internal';
import { round } from 'lodash-es';
import { useImmer } from 'use-immer';
import { buildFormData } from '~/lib/file';
import { mutableReplaceOrAdd } from '~/lib/jsTools/array';

interface UseFileUploaderConfigs {
  url?: string;
  httpClient: AxiosInstance;
}

export interface UseFileUploaderEvents<ResponseBody> {
  onUploadSucceed?: (response: ResponseBody) => void;
  onSubmit?: (responses: ResponseBody[]) => void;
}

export type UploadFileFunc<RequestBody> = (id: string, body: RequestBody) => void;
export type RetryUploadFileFunc = (id: string) => void;
export type RemoveUploadFileFunc = (id: string) => void;
export type SubmitFunc = () => void;

export interface UploadFileStatus<ResponseBody, RequestBody> {
  id: string;
  filename: string;
  filetype: string;
  filesize: number;
  file?: File;
  progress: number;
  done: boolean;
  error?: Error;
  abortController: AbortController;
  requestBody: RequestBody;
  response?: ResponseBody;
}

export interface UseFileUploaderReturn<ResponseBody, RequestBody> {
  uploadFileStatus: UploadFileStatus<ResponseBody, RequestBody>[];
  upload: UploadFileFunc<RequestBody>;
  retry: RetryUploadFileFunc;
  remove: RemoveUploadFileFunc;
  submit: SubmitFunc;
}

const findFileInRequestBody = (body: any): File | undefined => {
  for (const value of Object.values(body)) {
    if (value instanceof File) {
      return value;
    }
  }
};

export const useFileUploader = <ResponseBody, RequestBody>({
  url = '',
  httpClient,
  onUploadSucceed: propsOnUploadSucceed,
  onSubmit: propOnSubmit
}: UseFileUploaderConfigs & UseFileUploaderEvents<ResponseBody>): UseFileUploaderReturn<ResponseBody, RequestBody> => {
  const [uploadFileStatus, setUploadFileStatus] = useImmer<UploadFileStatus<ResponseBody, RequestBody>[]>([]);
  const updateUploadFileStatus = (
    id: string,
    update: (status: WritableDraft<UploadFileStatus<ResponseBody, RequestBody>>) => void
  ) => {
    setUploadFileStatus((draft) => {
      const status = draft.find((status) => status.id === id);
      if (status) {
        update(status);
      }
    });
  };

  // TODO: should switch to useEvent instead of useMemoizedFn after the RFC is adopted (https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md)

  const onUploadSucceed = useMemoizedFn<NonNullable<UseFileUploaderEvents<ResponseBody>['onUploadSucceed']>>(
    (response) => propsOnUploadSucceed?.(response)
  );

  const onSubmit = useMemoizedFn<NonNullable<UseFileUploaderEvents<ResponseBody>['onSubmit']>>((responses) =>
    propOnSubmit?.(responses)
  );

  const upload = useMemoizedFn<UploadFileFunc<RequestBody>>((id, body) => {
    const file = findFileInRequestBody(body);
    if (!file) {
      return;
    }
    const abortController = new AbortController();

    setUploadFileStatus((draft) => {
      mutableReplaceOrAdd(draft, (status) => status.id === id, {
        id,
        abortController,
        progress: 0,
        done: false,
        filename: file.name,
        filesize: file.size,
        filetype: file.type,
        requestBody: body as Draft<RequestBody>
      });
    });

    httpClient
      .request<ResponseBody>({
        url,
        method: 'POST',
        data: buildFormData(body),
        onUploadProgress: (event: ProgressEvent) => {
          updateUploadFileStatus(id, (status) => {
            status.progress = round(event.loaded / event.total, 2);
          });
        },
        signal: abortController.signal
      })
      .then(({ data }) => {
        updateUploadFileStatus(id, (status) => {
          status.done = true;
          status.response = data as Draft<ResponseBody>;
        });
        onUploadSucceed(data);
      })
      .catch((error) => {
        updateUploadFileStatus(id, (status) => {
          status.error = error;
        });
      });
  });

  const retry = useMemoizedFn<RetryUploadFileFunc>((id) => {
    const status = uploadFileStatus.find((status) => status.id === id);
    if (!status) {
      return;
    }
    upload(id, status.requestBody);
  });

  const remove = useMemoizedFn<RemoveUploadFileFunc>((id) => {
    const status = uploadFileStatus.find((status) => status.id === id);
    if (!status) {
      return;
    }
    if (!status.done) {
      status.abortController.abort();
    }
    setUploadFileStatus((draft) => draft.filter((status) => status.id !== id));
  });

  const submit = useMemoizedFn<SubmitFunc>(() => {
    onSubmit(uploadFileStatus.filter((status) => status.done).map((status) => status.response!));
    setUploadFileStatus((draft) => draft.filter((status) => !status.done));
  });

  return { uploadFileStatus, upload, retry, submit, remove };
};

export const useOfflineFileUploader = () => {
  const [fileStatus, setFileStatus] = useImmer<UploadFileStatus<null, null>[]>([]);

  // TODO: should switch to useEvent instead of useMemoizedFn after the RFC is adopted (https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md)

  const upload = useMemoizedFn((id: string, file: File) => {
    const abortController = new AbortController();

    setFileStatus((draft) => {
      mutableReplaceOrAdd(draft, (status) => status.id === id, {
        id,
        abortController,
        progress: 1,
        done: true,
        filename: file.name,
        filesize: file.size,
        filetype: file.type,
        file,
        requestBody: null
      });
    });
  });

  const remove = useMemoizedFn<RemoveUploadFileFunc>((id) => {
    setFileStatus((draft) => draft.filter((status) => status.id !== id));
  });

  const clear = useMemoizedFn(() => {
    setFileStatus([]);
  });

  return { uploadFileStatus: fileStatus, upload, remove, clear };
};
